import React, { useEffect, useState } from 'react';
import Heading from '../../../../../common/element/Heading';
import { setBreadcrumbs } from '../../../../../redux/Admin/breadcrumbsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyProspectDetail } from '../../../../../redux/ProspectCompany/companyProspectSlice';
import { selectCompanyProspectDetail, selectCompanyProspectDetailLoading } from '../../../../../redux/ProspectCompany/companyProspectSelectors';
import { useLocation, useNavigate } from 'react-router-dom';
import Overview from './overView';
import CompaniesEmployeesList from './CompanyEmployeesList';
import CompanyEmployeeView from './CompanyEmployeeView';

const ProspectCompanyView = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const tabFromQuery = query.get('tab');
  const tabSubFromQuery = query.get('subtab');
  const idFromQuery = query.get('id');

  const dispatch = useDispatch();
  const companyId = idFromQuery ? parseInt(idFromQuery, 10) : null;

  const companyDetail = useSelector(selectCompanyProspectDetail);
  const isLoading = useSelector(selectCompanyProspectDetailLoading);

  useEffect(() => {
    if (companyId) {
      dispatch(getCompanyProspectDetail(companyId));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    const companyName = companyDetail?.company_name || 'View Prospect Company';
    dispatch(
      setBreadcrumbs([
        {
          label: "Prospect Company",
          url: "/prospectCompany",
          home: "/companies",
        },
        {
          label: companyName,
          url: `/prospectCompany/view?id=${companyId}`,
        }
      ])
    );
  }, [companyDetail]);

  useEffect(() => {
    if (tabFromQuery) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  const handleBack = () => {
    const newQuery = new URLSearchParams(location.search);
    newQuery.delete('subtab');
    newQuery.set('tab', 'employees');
    navigate(`${location.pathname}?${newQuery.toString()}`);
  };

  const addressParts = companyDetail?.address?.split('/n') || [];
  const addressLine1 = addressParts[0] || '';
  const addressLine2 = addressParts.slice(1).join('/n') || '';

  return (
    <div>
      <div className="flex justify-between mb-5">
        {tabFromQuery === 'employees' && tabSubFromQuery === 'view' ? (
          <CompanyEmployeeView onBack={handleBack} companyId={companyId} candidateId={query.get('candidate_id')} />
        ) : (
          <div className='w-full'>
            {/* Header Section with Avatar */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 font-bold text-xl">
                {companyDetail?.company_name?.charAt(0)?.toUpperCase() || 'P'}
              </div>
              <div>
                <div className="w-80 h-10 text-start">
                  <Heading className='text-nowrap'>{companyDetail?.company_name || '—'}</Heading>
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <div className={`flex items-center gap-1 px-3 py-0.5 rounded-[16px] ${companyDetail?.is_archived ? 'bg-red-50' : 'bg-[#ECFDF3]'}`}>
                    <div className={`w-2 h-2 rounded-full ${companyDetail?.is_archived ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    <span className={`font-lato font-medium text-[14px] ${companyDetail?.is_archived ? 'text-red-600' : 'text-[#027A48]'}`}>
                      {companyDetail?.is_archived ? 'Archived' : 'Active'}
                    </span>
                  </div>
                  {companyDetail?.contact_email && (
                    <span className="font-lato font-medium text-[14px] text-[#1D2939]">
                      {companyDetail.contact_email}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-0 p-1 bg-[#FFF4E5] border border-[#FFF4E5] rounded-lg mb-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex-1 text-sm font-normal transition-colors py-2 px-3 rounded-md ${activeTab === 'overview'
                  ? 'text-gray-900 bg-white shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]'
                  : 'text-gray-600 bg-[#FFF4E5]'
                  }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('employees')}
                className={`flex-1 text-sm font-normal transition-colors py-2 px-3 rounded-md ${activeTab === 'employees'
                  ? 'text-gray-900 bg-white shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]'
                  : 'text-gray-600 bg-[#FFF4E5]'
                  }`}
              >
                Employees
              </button>
            </div>

            {/* Conditional Tab Rendering */}
            {activeTab === 'overview' && (
              <Overview
                companyDetail={companyDetail}
                isLoading={isLoading}
                addressLine1={addressLine1}
                addressLine2={addressLine2}
              />
            )}

            {activeTab === 'employees' && (
              <CompaniesEmployeesList companyId={companyId} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProspectCompanyView;